import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Price } from '@artosera/ui';
import {
  comptesArtistesApi,
  useReleveArtiste,
  useValiderReleveArtiste,
  useEnvoyerReleveArtiste,
  useSupprimerReleveArtiste,
  type ResultatEnvoi,
} from '../../api/comptesArtistes.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { EcrituresCompte, messageCompte } from './compteUi.js';
import { ReversementDialog } from './ReversementDialog.js';
import styles from './ComptesArtistes.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

export function ReleveDetailPage() {
  const { id = '' } = useParams();
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const label = (cle: string) => t(`regie:comptesArtistes.${cle}`);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const lecture = useReleveArtiste(galerieSlug, id);
  const valider = useValiderReleveArtiste(galerieSlug);
  const envoyer = useEnvoyerReleveArtiste(galerieSlug);
  const supprimer = useSupprimerReleveArtiste(galerieSlug);
  const [confirmation, setConfirmation] = useState<'valider' | 'supprimer' | null>(null);
  const [reversement, setReversement] = useState(false);
  const [resultat, setResultat] = useState<ResultatEnvoi | null>(null);
  const [erreur, setErreur] = useState('');
  const peutValider = galerieALaction(galerie, 'artist-accounts:validate');
  const peutEcrire = galerieALaction(galerie, 'artist-accounts:write');
  const r = lecture.data?.releve;
  async function pdf() {
    setErreur('');
    try {
      const reponse = await dansLaPorteeCourante((a) =>
        comptesArtistesApi.pdfReleve(galerieSlug, id, a),
      );
      ouvrirUrlSignee(reponse.url);
    } catch (e) {
      setErreur(messageCompte(e, t('commun:erreurReseau')));
    }
  }
  async function confirmer() {
    if (!r) return;
    setErreur('');
    try {
      if (confirmation === 'valider') await valider.mutateAsync({ id, versionAttendue: r.version });
      else {
        await supprimer.mutateAsync(id);
        navigate(`/${galerieSlug}/comptes-artistes`);
      }
      setConfirmation(null);
    } catch (e) {
      setErreur(messageCompte(e, t('commun:erreurReseau')));
    }
  }
  return (
    <div data-ui="page" className={styles.page}>
      <Link to={`/${galerieSlug}/comptes-artistes`}>{label('titre')}</Link>
      <h1>{r?.numero ?? label('releve')}</h1>
      {lecture.isLoading && <p aria-busy="true">{t('commun:chargement')}</p>}
      {lecture.isError && (
        <p role="alert">{messageCompte(lecture.error, t('commun:erreurReseau'))}</p>
      )}
      {erreur && !confirmation && <p role="alert">{erreur}</p>}
      {r && (
        <>
          <p>
            <Link to={`/${galerieSlug}/artistes/${r.artisteId}`}>{label('artiste')}</Link> ·{' '}
            {r.debut.slice(0, 10)} → {r.fin.slice(0, 10)} ({label('finExclue')}) ·{' '}
            {label(`etats.${r.etat}`)}
          </p>
          <div className={styles.actions}>
            {r.etat === 'brouillon' && (
              <>
                {peutValider && (
                  <Button
                    onClick={() => {
                      setErreur('');
                      setConfirmation('valider');
                    }}
                  >
                    {label('valider')}
                  </Button>
                )}
                <Verrouillable action="artist-accounts:write" ouvert={peutEcrire}>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setErreur('');
                      setConfirmation('supprimer');
                    }}
                  >
                    {label('supprimer')}
                  </Button>
                </Verrouillable>
              </>
            )}
            {r.etat === 'valide' && (
              <>
                <Button onClick={() => void pdf()}>{label('pdf')}</Button>
                {peutValider && (
                  <Button
                    loading={envoyer.isPending}
                    onClick={() => {
                      setErreur('');
                      setResultat(null);
                      void envoyer
                        .mutateAsync(id)
                        .then((r) => setResultat(r.envoi.resultat))
                        .catch((e: unknown) =>
                          setErreur(messageCompte(e, t('commun:erreurReseau'))),
                        );
                    }}
                  >
                    {label('envoyer')}
                  </Button>
                )}
              </>
            )}
            {peutValider && r.etat === 'valide' && (
              <Button onClick={() => setReversement(true)}>{label('saisirReversement')}</Button>
            )}
          </div>
          {resultat && (
            <p role={resultat === 'echec' ? 'alert' : 'status'}>{label(`envois.${resultat}`)}</p>
          )}
          <EcrituresCompte ecritures={r.ecritures} />
          <dl className={styles.totaux}>
            <dt>{label('soldeReporte')}</dt>
            <dd>
              <Price cents={r.soldeReporteCentimes} lang={langue} />
            </dd>
            {r.totaux &&
              (
                [
                  'soldePeriodeCentimes',
                  'soldeFinCentimes',
                  'imputationsCentimes',
                  'soldeAVerserCentimes',
                ] as const
              ).map((cle) => (
                <div key={cle}>
                  <dt>{label(cle)}</dt>
                  <dd>
                    <Price cents={r.totaux![cle]} lang={langue} />
                  </dd>
                </div>
              ))}
          </dl>
          <section>
            <h2>{label('imputations')}</h2>
            <ul>
              {r.imputations.map((i) => (
                <li key={i.avanceId}>
                  <Link to={`/${galerieSlug}/comptes-artistes/avances/${i.avanceId}`}>
                    {label('avance')}
                  </Link>{' '}
                  · <Price cents={i.montantCentimes} lang={langue} />
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h2>{label('avances')}</h2>
            <ul>
              {r.avances.map((a) => (
                <li key={a.id}>
                  <Link to={`/${galerieSlug}/comptes-artistes/avances/${a.id}`}>
                    {a.date.slice(0, 10)} ·{' '}
                    {r.etat === 'brouillon' ? label(`natures.${a.nature}`) : a.nature}
                  </Link>{' '}
                  · {label('restantDu')} : <Price cents={a.restantDuCentimes} lang={langue} />
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h2>{label('historiqueEnvois')}</h2>
            <ul>
              {r.envois.map((e, index) => (
                <li key={`${e.le}:${index}`}>
                  {e.le.slice(0, 10)} · {e.destinataire} · {label(`envois.${e.resultat}`)}
                </li>
              ))}
            </ul>
          </section>
          {confirmation && (
            <Dialog
              open
              onOpenChange={(v) => {
                if (!v && !valider.isPending && !supprimer.isPending) setConfirmation(null);
              }}
              title={label(confirmation === 'valider' ? 'confirmerValidation' : 'supprimer')}
              description={label(confirmation === 'valider' ? 'validationAide' : 'suppressionAide')}
              closeLabel={t('commun:fermer')}
              footer={
                <Button
                  loading={valider.isPending || supprimer.isPending}
                  onClick={() => void confirmer()}
                >
                  {label('confirmer')}
                </Button>
              }
            >
              {erreur && <p role="alert">{erreur}</p>}
            </Dialog>
          )}
          {reversement && (
            <ReversementDialog
              key={id}
              galerieSlug={galerieSlug}
              artisteId={r.artisteId}
              montantPropose={r.totaux?.soldeAVerserCentimes ?? 0}
              onFermer={() => setReversement(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
